import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UploadIcon from '@mui/icons-material/CloudUpload';
import HistoryIcon from '@mui/icons-material/History';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import {
  migrationService,
  MigrationToolSummary,
  MigrationToolMapping,
} from '@services/migrationService';
import { organizationService } from '@services/organizationService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';

function MigrationWizardPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const [tools, setTools] = useState<MigrationToolSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [mapping, setMapping] = useState<MigrationToolMapping | null>(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const [, toolList] = await Promise.all([
        organizationService.getBySlug(slug),
        migrationService.listTools(),
      ]);
      setTools(toolList);
    } catch {
      setError(t('migration.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSelectTool = async (tool: string) => {
    setSelectedTool(tool);
    setMapping(null);
    setMappingLoading(true);
    setError(null);
    try {
      const result = await migrationService.getTool(tool);
      setMapping(result);
      setActiveStep(1);
    } catch {
      setError(t('migration.errors.mappingFailed'));
      setSelectedTool(null);
    } finally {
      setMappingLoading(false);
    }
  };

  /**
   * REV-732 slice 7: a destination per step, not a handler.
   *
   * This was the last pure-navigation NAMED handler in the codebase, and named
   * handlers are the class convertedLinks.guard.test.ts structurally cannot
   * see — it reads inline onClick attributes and cannot follow an identifier.
   * Which is why it survived five slices.
   */
  const actionHref = (action: string) => {
    if (action === 'bulk_register_contexts') return `/orgs/${slug}/contexts/bulk`;
    // History import is per-Context; send the user to the org's contexts list
    // to pick one, then use its "Import scan history" action (REV-249). The
    // fallback lands in the same place, so the two are deliberately one branch
    // rather than a duplicated destination that could drift apart.
    return `/orgs/${slug}`;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={320} height={48} />
        <Skeleton variant="rectangular" height={240} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error && tools.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        {/* REV-732: goes to a URL, so it is a link. */}
        <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} component={RouterLink} to="/orgs">
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  const orgHref = `/orgs/${slug}`;
  const importStepIcon = (action: string) => {
    if (action === 'bulk_register_contexts') return <UploadIcon />;
    if (action === 'import_scan_history') return <HistoryIcon />;
    return <CheckIcon />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('migration.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
        {t('migration.description')}
      </Typography>

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {/* Step 1 — pick the source tool */}
        <Step expanded>
          <StepLabel>{t('migration.steps.pickTool')}</StepLabel>
          <StepContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('migration.pickToolHelp')}
            </Typography>
            <Grid container spacing={2}>
              {tools.map((tool) => (
                <Grid item xs={12} sm={6} md={4} key={tool.tool}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: selectedTool === tool.tool ? 'primary.main' : undefined,
                      borderWidth: selectedTool === tool.tool ? 2 : 1,
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleSelectTool(tool.tool)}
                      aria-pressed={selectedTool === tool.tool}
                      sx={{ p: 2, height: '100%' }}
                    >
                      <Typography variant="h6" component="h2" gutterBottom>
                        {tool.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tool.description}
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </StepContent>
        </Step>

        {/* Step 2 — concept mapping */}
        <Step expanded={!!mapping}>
          <StepLabel>{t('migration.steps.reviewMapping')}</StepLabel>
          <StepContent>
            {mappingLoading && <Skeleton variant="rectangular" height={200} />}
            {mapping && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('migration.mappingHelp', { tool: mapping.label })}
                </Typography>
                <ScrollableTableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table
                    size="small"
                    aria-label={t('migration.mappingTableLabel', { tool: mapping.label })}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('migration.colSource', { tool: mapping.label })}</TableCell>
                        <TableCell>{t('migration.colRevenant')}</TableCell>
                        <TableCell>{t('migration.colNotes')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mapping.concepts.map((c) => (
                        <TableRow key={c.source}>
                          <TableCell>{c.source}</TableCell>
                          <TableCell>
                            <Chip size="small" label={c.revenant} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {c.notes}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollableTableContainer>
                <Stack direction="row" spacing={1}>
                  <Button onClick={() => setActiveStep(0)}>{t('migration.changeTool')}</Button>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => setActiveStep(2)}
                  >
                    {t('migration.startImport')}
                  </Button>
                </Stack>
              </>
            )}
          </StepContent>
        </Step>

        {/* Step 3 — guided import (composes existing flows) */}
        <Step expanded={activeStep >= 2 && !!mapping}>
          <StepLabel>{t('migration.steps.importData')}</StepLabel>
          <StepContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('migration.importHelp')}
            </Typography>
            <Stack spacing={2}>
              {(mapping?.importSteps ?? []).map((step) => (
                <Card key={step.key} variant="outlined">
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      justifyContent="space-between"
                    >
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                          {importStepIcon(step.action)}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" component="h3">
                            {step.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {step.description}
                          </Typography>
                        </Box>
                      </Box>
                      {step.action !== 'review' && (
                        <Button
                          variant="outlined"
                          component={RouterLink}
                          to={actionHref(step.action)}
                          sx={{ flexShrink: 0 }}
                        >
                          {step.action === 'bulk_register_contexts'
                            ? t('migration.actions.register')
                            : t('migration.actions.importHistory')}
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            <Box sx={{ mt: 3 }}>
              {/* REV-732: Finish only goes to the org page — it commits nothing,
                  so it is a link despite the label. */}
              <Button variant="contained" component={RouterLink} to={orgHref}>
                {t('migration.finish')}
              </Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
}

export default MigrationWizardPage;
